import Dexie, { type Table } from 'dexie';
import type {
  Animal, Schedule, FeedLog, Timer, Photo, Report, Profile, ChatMessageRow, DocumentRow,
} from '@/types';

/** Pending remote write, replayed in order by the outbox. */
export interface OutboxOp {
  seq?: number; // auto-increment
  created_at: string;
  table: string;
  op: 'insert' | 'upsert' | 'update' | 'delete';
  values?: Record<string, unknown>;
  match?: Record<string, unknown>;
  attempts: number;
}

export interface KV {
  key: string;
  value: unknown;
}

class TendDB extends Dexie {
  animals!: Table<Animal, string>;
  schedules!: Table<Schedule, string>;
  feed_logs!: Table<FeedLog, string>;
  timers!: Table<Timer, string>;
  photos!: Table<Photo, string>;
  reports!: Table<Report, string>;
  documents!: Table<DocumentRow, string>;
  messages!: Table<ChatMessageRow, string>;
  outbox!: Table<OutboxOp, number>;
  kv!: Table<KV, string>;

  constructor() {
    super('tend');
    // NB: profile lives in `kv` (key 'profile') — its Postgres PK is a
    // boolean, which IndexedDB cannot use as a key.
    this.version(2).stores({
      animals: 'id, status, name, updated_at',
      schedules: 'id, animal_id, next_due_at, updated_at',
      feed_logs: 'id, animal_id, schedule_id, fed_at',
      timers: 'id, due_at',
      photos: 'id, animal_id, taken_at',
      reports: 'id, created_at',
      documents: 'id, status, created_at',
      messages: 'id, day, created_at',
      profile: null,
      outbox: '++seq, created_at',
      kv: 'key',
    });
  }
}

export const db = new TendDB();

export async function getCachedProfile(): Promise<Profile | null> {
  const kv = await db.kv.get('profile');
  return (kv?.value as Profile) ?? null;
}

export async function setCachedProfile(p: Profile) {
  await db.kv.put({ key: 'profile', value: p });
}
